import { OrderResponseDto } from 'client/widget-components/ecom/types/EcomSDK';
import React from 'react';
import Divider from './components/Divider';
import OrderConfirmationHeader from './components/OrderConfirmationHeader';
import {
    OrderConfirmationStyles,
    OrderConfirmationTextStyles,
    resolveDesign,
} from './design';
import { DesignProvider } from './DesignContext';
import CancellationSection, {
    canRequestCancellation,
    isCancellationRequested,
} from './sections/CancellationSection';
import ContactSection from './sections/ContactSection';
import LineItemsSection from './sections/LineItemsSection';
import PaymentSection from './sections/PaymentSection';
import ShippingSection from './sections/ShippingSection';
import { OrderViewColumns, OrderViewContent } from './styles';
import { OrderConfirmationLayout } from './types';

interface OrderConfirmationContentProps {
    order: OrderResponseDto;
    isRefreshing: boolean;
    layout?: OrderConfirmationLayout;
    showIntro?: boolean;
    textStyles?: OrderConfirmationTextStyles;
    styles?: OrderConfirmationStyles;
}

const OrderConfirmationContent: React.FC<OrderConfirmationContentProps> = ({
    order,
    isRefreshing,
    layout = 'ONE_COLUMN',
    showIntro,
    textStyles,
    styles,
}) => {
    const hasShippingStep = !!order.shipping_method || !!order.pickup;
    // Pair shipping/pickup beside payment only when there is a shipping step to
    // pair with; otherwise payment falls back to full width (single column).
    const isTwoColumns = layout === 'TWO_COLUMNS' && hasShippingStep;
    // Default to showing the intro: only an explicit `false` (the "Without"
    // content setting) hides it. Legacy widgets persist no value (null/undefined)
    // and must keep showing the header.
    const introVisible = showIntro !== false;
    const showCancellation =
        canRequestCancellation(order) || isCancellationRequested(order);

    const design = React.useMemo(
        () => resolveDesign(textStyles, styles),
        [textStyles, styles]
    );

    return (
        <DesignProvider value={design}>
            <OrderViewContent
                data-auto='ssr-order-confirmation-widget'
                // Custom properties for the rules that cannot go through
                // ThemedText: every divider and bordered box, plus the text
                // roles reaching a form control, an HTML blob and the tags.
                style={
                    {
                        '--ocw-border-color': design.colors.dividers,
                        '--ocw-primary-text-color': design.colors.primaryText,
                        '--ocw-secondary-text-color':
                            design.colors.secondaryText,
                    } as React.CSSProperties
                }
            >
                {introVisible && (
                    <>
                        <OrderConfirmationHeader order={order} />
                        <Divider />
                    </>
                )}
                <LineItemsSection order={order} showIntro={introVisible} />
                <Divider />
                <ContactSection order={order} />
                {isTwoColumns ? (
                    <>
                        <Divider />
                        <OrderViewColumns data-auto='ssr-order-confirmation-columns'>
                            <ShippingSection order={order} />
                            <PaymentSection
                                order={order}
                                isRefreshing={isRefreshing}
                            />
                        </OrderViewColumns>
                    </>
                ) : (
                    <>
                        {hasShippingStep && (
                            <>
                                <Divider />
                                <ShippingSection order={order} />
                            </>
                        )}
                        <Divider />
                        <PaymentSection
                            order={order}
                            isRefreshing={isRefreshing}
                        />
                    </>
                )}
                {showCancellation && (
                    <>
                        <Divider />
                        <CancellationSection order={order} />
                    </>
                )}
            </OrderViewContent>
        </DesignProvider>
    );
};

export default OrderConfirmationContent;
